import * as React from 'react';

import PropTypes from 'prop-types';
import { branch, compose, getContext, renderComponent, setDisplayName, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';
import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';
import type { EventData, WithTrackingData } from 'js/lib/retracked';
import user from 'js/lib/user';

import BypassLogin from 'bundles/clips/components/short-form-content/BypassLogin';
import RedirectWithoutProgramSlug from 'bundles/clips/components/short-form-content/RedirectWithoutProgramSlug';
import RouteToLoginModal from 'bundles/clips/components/short-form-content/RouteToLoginModal';
import {
  withOrganizationId,
  withSFCVLPCourseDetailsData,
  withSFCVLPCourseMaterialsData,
  withSFCVideoData,
} from 'bundles/clips/components/short-form-content/SFCVLPGraphqlHOCs';
import type {
  PropsFromClipsCourseDetailsQuery,
  PropsFromSFCVLPCourseMaterialsQuery,
  PropsFromWithOrganizationId,
  ShortFormContentVideoAssets,
} from 'bundles/clips/components/short-form-content/SFCVLPGraphqlHOCs';
import SuggestedProgramRedirect from 'bundles/clips/components/short-form-content/SuggestedProgramRedirect';
import { getVideoTranscript } from 'bundles/clips/components/short-form-content/actions/getVideoTranscript';

type PropsFromGetContext = {
  _eventData: EventData;
  _withTrackingData: WithTrackingData;
};

export type PropsFromRouter = {
  pathname: string;
  courseSlug: string;
  itemId: string;
  programSlug?: string;
  orgSlug?: string;
  isShortFormContent: boolean;
  router: InjectedRouter;
  referringSkillId?: string;
  authProvider?: string;
  attemptSSOLogin?: boolean;
};

type PropsFromWithProps1 = {
  userId?: string;
};

export type PropsFromWithProps2 = {
  gqlLoading: boolean;
};

type PropsFromWithProps3 = {
  tracks: any;
  defaultTrack: any;
};

export type Props = PropsFromGetContext &
  PropsFromRouter &
  PropsFromWithProps1 &
  PropsFromClipsCourseDetailsQuery &
  PropsFromSFCVLPCourseMaterialsQuery &
  PropsFromWithProps2 &
  ShortFormContentVideoAssets &
  PropsFromWithProps3;

const branchSuggestedProgramRedirect = branch<Props>(
  ({ gqlLoading, suggestedToJoinProgram }) => !gqlLoading && !!suggestedToJoinProgram,
  renderComponent<PropsFromGetContext & PropsFromRouter & ShortFormContentVideoAssets>(
    ({ suggestedToJoinProgram, courseSlug, programSlug, orgSlug, itemId, router, _eventData, _withTrackingData }) => {
      return (
        <SuggestedProgramRedirect
          // see branch statement above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          suggestedToJoinProgram={suggestedToJoinProgram!}
          _eventData={_eventData}
          _withTrackingData={_withTrackingData}
          courseSlug={courseSlug}
          programSlug={programSlug}
          orgSlug={orgSlug}
          itemId={itemId}
          routerLocation={router.location}
        />
      );
    }
  )
);

const branchGraphqlErrorRedirectWithoutProgramSlug = branch<Props>(
  ({ programSlug, hasSFCVideoDataError }) => !!programSlug && !!hasSFCVideoDataError,
  renderComponent<PropsFromGetContext & PropsFromRouter & ShortFormContentVideoAssets>(
    ({ router, courseSlug, programSlug, itemId, _eventData, _withTrackingData }) => {
      return (
        <RedirectWithoutProgramSlug
          router={router}
          _eventData={_eventData}
          _withTrackingData={_withTrackingData}
          courseSlug={courseSlug}
          programSlug={programSlug}
          itemId={itemId}
        />
      );
    }
  )
);

const branchNotLoggedInAttemptSSO = branch<PropsFromRouter & PropsFromWithProps1>(
  ({ userId, authProvider, attemptSSOLogin }) => !userId && !!authProvider && !!attemptSSOLogin,
  compose(
    withOrganizationId,
    branch<PropsFromWithOrganizationId>(
      ({ orgId }) => !!orgId,
      renderComponent<PropsFromWithOrganizationId>(({ orgId }) => <BypassLogin orgId={orgId as string} />)
    )
  )
);

const branchNotLoggedInShowLoginModal = branch<PropsFromWithProps1>(
  ({ userId }) => !userId,
  renderComponent<PropsFromRouter & PropsFromGetContext>(
    ({ router, courseSlug, programSlug, orgSlug, itemId, _eventData, _withTrackingData }) => (
      <RouteToLoginModal
        router={router}
        _eventData={_eventData}
        _withTrackingData={_withTrackingData}
        courseSlug={courseSlug}
        programSlug={programSlug}
        orgSlug={orgSlug}
        itemId={itemId}
      />
    )
  )
);

// TODO: moved this HOC from `video-logged-out-page/components/short-form-content/withSFCVLPData.tsx` here
// so that we can retire that directory.
// This HOC is only used by `ClipTabs.tsx` to show the transcript languages.
// In fact, there's a new way of getting the transcript language list (showed in the `clips` bundle).
// We should consider using the components in `clips` to replace the `ClipTabs` component.
export const branchDefaultTrack = branch<Props>(
  ({ defaultTrack }) => !!defaultTrack,
  compose<PropsFromWithProps3, Props>(
    connectToFluxibleContext<PropsFromWithProps3>(({ executeAction }, { defaultTrack }) => {
      executeAction(getVideoTranscript, defaultTrack);
    }),
    connectToStores<PropsFromWithProps3, Props>(['VideoTranscriptStore'], (store, props) => {
      const trackData = store.VideoTranscriptStore.getData();
      return {
        ...props,
        track: trackData,
      };
    })
  )
);

export const enhancer = compose<Props, {}>(
  setDisplayName('withSFCVLPData'),
  getContext({
    _eventData: PropTypes.object,
    _withTrackingData: PropTypes.func,
  }),
  connectToRouter((router) => {
    const {
      location: {
        pathname,
        query: { program, org, authProvider, attemptSSOLogin },
        search,
      },
      params: { courseSlug, itemId },
    } = router;

    const queryParams = new URLSearchParams(search);
    const referringSkillId = queryParams.get('referringSkillId')?.toString();

    return {
      router,
      pathname,
      courseSlug,
      itemId,
      programSlug: program,
      orgSlug: org,
      isShortFormContent: true,
      referringSkillId,
      authProvider,
      attemptSSOLogin: attemptSSOLogin === 'true',
    };
  }),
  withProps<PropsFromWithProps1, PropsFromRouter>(() => ({
    userId: user.isAuthenticatedUser() ? user.get().id?.toString() : undefined,
  })),
  branchNotLoggedInAttemptSSO,
  branchNotLoggedInShowLoginModal,
  withSFCVLPCourseDetailsData,
  withSFCVideoData,
  withSFCVLPCourseMaterialsData,
  withProps<
    PropsFromWithProps2,
    PropsFromClipsCourseDetailsQuery & PropsFromSFCVLPCourseMaterialsQuery & ShortFormContentVideoAssets
  >(({ courseMaterialsQueryDone, courseDetailsQueryDone, videoDataDone }) => {
    const allQueriesDone = courseMaterialsQueryDone && courseDetailsQueryDone && videoDataDone;
    return { gqlLoading: !allQueriesDone };
  }),
  branchGraphqlErrorRedirectWithoutProgramSlug,
  branchSuggestedProgramRedirect,
  Retracked.createTrackedContainer<Props>(
    ({
      courseId,
      programSlug,
      orgSlug,
      itemId,
      accessProvidedViaProgram,
      referringSkillId,
      currentVideoItem,
      xdpApiData,
    }) => {
      const xdpMetadata = xdpApiData?.xdpMetadata;
      const partnerName =
        xdpMetadata?.__typename === 'XdpV1_cdpMetadataMember'
          ? xdpMetadata?.cdpMetadata?.partners?.[0]?.name
          : undefined;
      return {
        courseId,
        clipName: currentVideoItem?.name,
        partnerName,
        programSlug: programSlug || (!orgSlug ? accessProvidedViaProgram?.slug : undefined),
        orgSlug,
        itemId,
        referringSkillId,
      };
    }
  )
);

export default enhancer;
